import { ReactNode, useState } from 'react';
import { useQuery } from 'react-query';
import { api } from '../../services/api';
import { Lead } from '../ui/Panel';
import Button from '../ui/Button';
import Help from '../ui/Help';
import { SkeletonRows, ErrorNotice, InfoNotice } from '../ui/States';
import { fmt } from '../../lib/format';
import { torontoToday } from '../../lib/events';
import { errorMessage } from '../../lib/programs';
import type { Dashboard, Metric, PanelProps } from './types';
import { PeopleSheet, useInvitees } from './PeopleList';
import { slotFor } from './registry';
import { headRow, th, td, num, weekDay } from './logistics';

const money = (cents: number | null) =>
  cents === null ? null : (cents / 100).toLocaleString('en-CA', { style: 'currency', currency: 'CAD' });

/** Shorter labels for the dashboard's metrics, by key (the API's labels stay as they are). */
const LABEL: Record<string, string> = {
  in_person: 'In person',
  local: 'Local, within the hotel threshold',
  virtual: 'Online',
  dietary: 'Dietary needs',
};
const labelOf = (m: Metric) => LABEL[m.key] ?? m.label;

const plural = (n: number, one: string, many: string) => `${fmt(n)} ${n === 1 ? one : many}`;
const andList = (parts: string[]) =>
  parts.length < 2 ? (parts[0] ?? '') : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;

const Dash = () => <span className="text-ink-3">–</span>;
const h2 = 'font-serif font-semibold text-heading text-ink';
const noteCls = 'font-sans font-normal text-[15px] text-ink-3';
const sectionRule = 'mt-14 pt-6 border-t border-line-strong max-md:mt-9 max-md:pt-4';

/** The summary of an in-person event. Admins open the people behind each number in a side sheet. */
export default function DashboardPanel(props: PanelProps) {
  const { detail, go } = props;
  const e = detail.event;
  const [picked, setPicked] = useState<Metric | null>(null);
  const q = useQuery<Dashboard>(
    ['event-dashboard', e.id],
    async () => (await api.get(`/events/${e.id}/dashboard`)).data.data,
    { refetchInterval: 60000 },
  );
  const invitees = useInvitees(e.id, Boolean(q.data?.idsVisible));
  const Setup = slotFor('overviewSetup');
  if (q.isLoading) return <SkeletonRows rows={8} />;
  if (q.error || !q.data) return <ErrorNotice>{errorMessage(q.error, 'Could not load the dashboard.')}</ErrorNotice>;
  const d = q.data;
  const ended = new Date(e.endsAt) < new Date();
  const started = Boolean(e.days[0]) && torontoToday() >= e.days[0].date;
  const pick = d.idsVisible ? (m: Metric) => setPicked(m) : null;
  const metric = (key: string) => d.attendance.find((m) => m.key === key);
  const val = (key: string) => metric(key)?.value ?? 0;

  /** A count that opens the people behind it (admins), or the plain number. */
  const count = (m: Metric | undefined, text?: ReactNode) => {
    if (!m) return null;
    const shown = text ?? fmt(m.value);
    return pick && m.ids?.length && m.value ? (
      <button
        type="button"
        className="tabular underline decoration-line-strong underline-offset-[3px] hover:decoration-ink text-inherit"
        onClick={() => pick(m)}
      >
        {shown}
      </button>
    ) : (
      <span className="tabular">{shown}</span>
    );
  };
  /** A row label that opens the same people as its number. */
  const name = (m: Metric) =>
    pick && m.ids?.length && m.value ? (
      <button
        type="button"
        className="text-left text-ink underline decoration-line underline-offset-[3px] hover:decoration-ink"
        onClick={() => pick(m)}
      >
        {labelOf(m)}
      </button>
    ) : (
      labelOf(m)
    );

  // ---- lead
  const coming = val('in_person') + val('virtual');
  let lead: string;
  if (ended) {
    const attended = detail.byStatus.attended ?? 0;
    const days = d.byDay.filter((x) => x.checkedIn.value);
    lead = attended
      ? `${plural(attended, 'person', 'people')} attended${
          days.length
            ? `: ${andList(days.map((x) => `${fmt(x.checkedIn.value)} checked in on ${weekDay(x.date)}`))}`
            : ''
        }.`
      : days.length
        ? `${andList(days.map((x) => `${fmt(x.checkedIn.value)} checked in on ${weekDay(x.date)}`))}.`
        : 'Nobody is marked as attended yet.';
    const noShow = detail.byStatus.no_show ?? 0;
    if (noShow) lead += ` ${plural(noShow, 'person', 'people')} registered but didn't come.`;
  } else {
    const how = andList(
      [
        val('in_person') ? `${fmt(val('in_person'))} in person` : '',
        val('virtual') ? `${fmt(val('virtual'))} online` : '',
      ].filter(Boolean),
    );
    lead = coming
      ? `${fmt(coming)} ${coming === 1 ? 'person is' : 'people are'} coming: ${how}.`
      : 'Nobody has said they are coming yet.';
    const rest = [
      val('no_answer') ? `${fmt(val('no_answer'))} ${val('no_answer') === 1 ? 'hasn’t' : 'haven’t'} answered yet` : '',
      d.openFollowups ? `${plural(d.openFollowups, 'follow-up is', 'follow-ups are')} open` : '',
    ].filter(Boolean);
    if (rest.length) {
      const s = rest.join(', and ');
      lead += ` ${s.charAt(0).toUpperCase()}${s.slice(1)}.`;
    }
  }

  // ---- who is coming
  const who = d.attendance.filter(
    (m) => m.key !== 'responses' && (m.value || ['in_person', 'virtual', 'not_attending'].includes(m.key)),
  );
  const travel = d.travel.filter((m) => m.value || !['other_travel', 'travel_unknown'].includes(m.key));
  const responses = metric('responses');

  // By audience: on the list, and (for admins, from the list) how many of them are coming.
  const comingIds = new Set([...(metric('in_person')?.ids ?? []), ...(metric('virtual')?.ids ?? [])]);
  const byAudience = e.audiences
    .map((a) => {
      const people = (invitees.data?.invitees ?? []).filter((i) => i.audienceKey === a.key).map((i) => i.id);
      return {
        a,
        m: {
          key: `audience:${a.key}`,
          label: a.name,
          value: detail.byAudience[a.key] ?? 0,
          ids: invitees.data ? people : undefined,
        } as Metric,
        coming: invitees.data ? people.filter((id) => comingIds.has(id)).length : null,
      };
    })
    .filter((x) => x.m.value);

  // ---- how they registered
  const channels = d.channels.map((c) => (c.key === 'no_response' ? { ...c, label: 'No answer yet' } : c));
  const sum = (k: 'inPerson' | 'online' | 'notAttending' | 'total') => channels.reduce((n, c) => n + c[k], 0);
  const cell = (n: number) => (n ? fmt(n) : <Dash />);
  const breakdown = (parts: [number, string][]) =>
    parts
      .filter(([n]) => n)
      .map(([n, w]) => `${fmt(n)} ${w}`)
      .join(' · ');

  // ---- days and nights
  const dayLabelOf = (x: Dashboard['byDay'][number]) => `${weekDay(x.date)}${x.label ? ` · ${x.label}` : ''}`;
  const dayCols: { k: 'inPerson' | 'headcount' | 'virtual' | 'notAttending' | 'checkedIn'; head: string }[] = [
    { k: 'inPerson', head: 'In person, answered' },
    { k: 'headcount', head: 'Estimated headcount' },
    { k: 'virtual', head: 'Online' },
    { k: 'notAttending', head: 'Not attending' },
    ...(started ? [{ k: 'checkedIn' as const, head: 'Checked in' }] : []),
  ];
  const dayCount = (x: Dashboard['byDay'][number], m: Metric) =>
    m.value ? count({ ...m, label: `${weekDay(x.date)}: ${m.label}` }) : <Dash />;
  const nightMetric = (n: Dashboard['rooms']['nights'][number]): Metric => ({
    key: `night:${n.night}`,
    label: `Rooms, night of ${weekDay(n.night)}`,
    value: n.rooms,
    ids: n.ids,
  });
  const r = d.rooms;
  const hotelLink = go ? (
    <button
      type="button"
      className="text-accent underline underline-offset-[3px] hover:text-ink"
      onClick={() => go('before', 'hotel')}
    >
      Hotel
    </button>
  ) : null;

  const table2 = (head: string, list: Metric[], foot?: ReactNode) => (
    <table className="w-full tabular max-md:table">
      <thead>
        <tr className={headRow}>
          <th className={th}>{head}</th>
          <th className={`${th} ${num}`}>People</th>
        </tr>
      </thead>
      <tbody>
        {list.map((m) => (
          <tr key={m.key} className="border-b border-line">
            <td className={`${td} ${m.indent ? 'pl-5 text-ink-2' : ''}`}>{name(m)}</td>
            <td className={`${td} ${num}`}>{count(m)}</td>
          </tr>
        ))}
      </tbody>
      {foot}
    </table>
  );

  return (
    <div>
      <Lead lead={lead} sub="The summary of this event. Any number opens the people behind it.">
        {!ended && d.openFollowups && go ? (
          <Button variant="primary" onClick={() => go('before', 'followups')}>
            Review the {fmt(d.openFollowups)} follow-ups <span aria-hidden="true">→</span>
          </Button>
        ) : null}
      </Lead>
      {!d.idsVisible ? (
        <div className="mt-6">
          <InfoNotice>Numbers only: names are shown to admins.</InfoNotice>
        </div>
      ) : null}

      <div className="mt-10 pt-6 border-t border-line-strong flex flex-wrap gap-x-16 gap-y-12 max-md:mt-8 max-md:pt-4 max-md:gap-y-9">
        <section className="flex-[1_1_440px] min-w-0 max-md:basis-full">
          <h2 className={`${h2} mb-3`}>{ended ? 'Who was expected' : 'Who is coming'}</h2>
          {table2(
            'Answer',
            who,
            <tfoot>
              <tr className="border-t border-line-strong font-semibold">
                <td className={`${td} font-semibold`}>On the list</td>
                <td className={`${td} ${num} font-semibold`}>{fmt(d.invitees)}</td>
              </tr>
            </tfoot>,
          )}
          <p className="mt-4 text-[15px] leading-[22px] text-ink-2">
            {byAudience.length ? (
              <>
                By audience:{' '}
                {byAudience.map((x, i) => (
                  <span key={x.a.key}>
                    {i ? ' · ' : ''}
                    {count(x.m, `${x.a.name} ${fmt(x.m.value)}`)}
                    {x.coming !== null ? ` (${fmt(x.coming)} coming)` : ''}
                  </span>
                ))}
                .
                <br />
              </>
            ) : null}
            {d.speakers ? (
              <>
                {count(d.speakers.listed, `Speakers ${fmt(d.speakers.listed.value)}`)}
                {d.speakers.external.value ? (
                  <>
                    , of whom{' '}
                    {count(
                      d.speakers.external,
                      `${fmt(d.speakers.external.value)} ${d.speakers.external.value === 1 ? 'hasn’t' : 'haven’t'} sent the form`,
                    )}
                  </>
                ) : null}
                .{' '}
              </>
            ) : null}
            {responses ? (
              responses.value ? (
                <>{count(responses, `Form responses ${fmt(responses.value)}`)} (latest per person).</>
              ) : (
                'No form responses yet.'
              )
            ) : null}
          </p>
        </section>

        <section className="flex-[1_1_360px] min-w-0 max-md:basis-full">
          <h2 className={`${h2} mb-3`}>
            Travel and needs <span className={noteCls}>· in person</span>
          </h2>
          {table2('Need', travel)}
        </section>
      </div>

      <section className={sectionRule}>
        <h2 className={`${h2} mb-3`}>
          How they registered <span className={noteCls}>· each person counted once</span>
        </h2>
        <table className="w-full tabular max-md:hidden">
          <thead>
            <tr className={headRow}>
              <th className={th}>Came from</th>
              <th className={`${th} ${num} w-[120px]`}>In person</th>
              <th className={`${th} ${num} w-[120px]`}>Online</th>
              <th className={`${th} ${num} w-[140px]`}>Not attending</th>
              <th className={`${th} ${num} w-[120px]`}>People</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((c) => (
              <tr key={c.key} className="border-b border-line">
                <td className={td}>{c.label}</td>
                <td className={`${td} ${num}`}>{cell(c.inPerson)}</td>
                <td className={`${td} ${num}`}>{cell(c.online)}</td>
                <td className={`${td} ${num}`}>{cell(c.notAttending)}</td>
                <td className={`${td} ${num}`}>{cell(c.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-line-strong">
              <td className={`${td} font-semibold`}>Total</td>
              <td className={`${td} ${num} font-semibold`}>{fmt(sum('inPerson'))}</td>
              <td className={`${td} ${num} font-semibold`}>{fmt(sum('online'))}</td>
              <td className={`${td} ${num} font-semibold`}>{fmt(sum('notAttending'))}</td>
              <td className={`${td} ${num} font-semibold`}>{fmt(sum('total'))}</td>
            </tr>
          </tfoot>
        </table>
        <ul className="md:hidden tabular">
          {channels.map((c) => (
            <li key={c.key} className="flex justify-between gap-4 border-b border-line py-[11px]">
              <span className="text-ink">
                {c.label}
                <span className="block text-small text-ink-3">
                  {breakdown([
                    [c.inPerson, 'in person'],
                    [c.online, 'online'],
                    [c.notAttending, 'not attending'],
                  ])}
                </span>
              </span>
              <span className="text-ink">{fmt(c.total)}</span>
            </li>
          ))}
          <li className="flex justify-between gap-4 py-[11px] font-semibold text-ink">
            <span>Total</span>
            <span>{fmt(sum('total'))}</span>
          </li>
        </ul>
        {d.eventbrite ? (
          <p className="mt-4 text-[15px] leading-[22px] text-ink-2">
            Ticket sales on Eventbrite:{' '}
            {count(
              { ...d.eventbrite.buyers, label: 'Eventbrite buyers' },
              plural(d.eventbrite.tickets, 'ticket', 'tickets'),
            )}
            {money(d.eventbrite.grossCents) ? ` · ${money(d.eventbrite.grossCents)} gross` : ''}
            {money(d.eventbrite.netCents) ? ` · ${money(d.eventbrite.netCents)} net` : ''}.
          </p>
        ) : null}
      </section>

      {d.byDay.length || r.nights.length ? (
        <section className={sectionRule}>
          <h2 className={`${h2}`}>
            Days and nights <span className={noteCls}>· estimated</span>
          </h2>
          <div className="mt-2">
            <Help label="How we count">
              <p>
                In person counts people who answered they come in person, on the days their audience attends. The
                estimate adds speakers who have not answered, on their session days (every day when their sessions are
                not in yet). Guest speakers count on their session days. Eventbrite buyers count once they are on the
                list. Not attending: people who declined.
              </p>
              {r.estimated ? (
                <p className="mt-2">
                  Hotel rooms: without hotel dates, a room request counts on the night before each day the person
                  attends.
                </p>
              ) : null}
            </Help>
          </div>
          <div className="flex flex-wrap gap-x-16 gap-y-8 mt-1">
            {d.byDay.length ? (
              <div className="flex-[3_1_520px] min-w-0 max-md:basis-full">
                <table className="w-full tabular max-md:hidden">
                  <thead>
                    <tr className={headRow}>
                      <th className={th}>Day</th>
                      {dayCols.map((c) => (
                        <th key={c.k} className={`${th} ${num}`}>
                          {c.head}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {d.byDay.map((x) => (
                      <tr key={x.date} className="border-b border-line">
                        <td className={td}>{dayLabelOf(x)}</td>
                        {dayCols.map((c) => (
                          <td key={c.k} className={`${td} ${num}`}>
                            {dayCount(x, x[c.k])}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="md:hidden text-small text-ink-3 mb-1">Estimated headcount per day</p>
                <ul className="md:hidden tabular">
                  {d.byDay.map((x) => (
                    <li key={x.date} className="flex justify-between gap-4 border-b border-line py-[11px]">
                      <span className="text-ink">
                        {dayLabelOf(x)}
                        <span className="block text-small text-ink-3">
                          {breakdown([
                            [x.inPerson.value, 'answered in person'],
                            [x.virtual.value, 'online'],
                            [x.notAttending.value, 'not attending'],
                            ...(started ? ([[x.checkedIn.value, 'checked in']] as [number, string][]) : []),
                          ])}
                        </span>
                      </span>
                      <span className="text-ink">{dayCount(x, x.headcount)}</span>
                    </li>
                  ))}
                </ul>
                {!started ? (
                  <p className="mt-3 text-small text-ink-3">Check-in counts appear here once the event starts.</p>
                ) : null}
              </div>
            ) : null}
            {r.nights.length ? (
              <div className="flex-[2_1_320px] min-w-0 max-md:basis-full">
                <table className="w-full tabular max-md:table">
                  <thead>
                    <tr className={headRow}>
                      <th className={th}>Hotel night</th>
                      <th className={`${th} ${num}`}>Rooms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.nights.map((n) => (
                      <tr key={n.night} className="border-b border-line">
                        <td className={td}>
                          {weekDay(n.night)}
                          {n.inBlock ? null : <span className="text-ink-2"> · outside the block</span>}
                        </td>
                        <td className={`${td} ${num}`}>{n.rooms ? count(nightMetric(n)) : <Dash />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-3 text-[15px] leading-[22px] text-ink-2">
                  {r.block === null ? (
                    <>
                      Busiest night uses {plural(r.peak, 'room', 'rooms')}. Set the room block on Hotel to see how many
                      are left.
                    </>
                  ) : r.left !== null && r.left < 0 ? null : (
                    <>
                      Busiest night uses {fmt(r.peak)} of the {plural(r.block, 'room', 'rooms')} in the block, with{' '}
                      {fmt(r.left ?? 0)} to spare.
                    </>
                  )}{' '}
                  {hotelLink}
                </p>
                {r.block !== null && r.left !== null && r.left < 0 ? (
                  <div className="mt-3">
                    <ErrorNotice>
                      <strong className="font-semibold">
                        The hotel block is {plural(-r.left, 'room', 'rooms')} short on the busiest night.
                      </strong>{' '}
                      {fmt(r.peak)} rooms are counted and the block holds {fmt(r.block)}. Ask the hotel for more, or
                      check the room requests on Hotel.
                    </ErrorNotice>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {Setup ? <Setup {...props} /> : null}

      {picked && picked.ids ? (
        <PeopleSheet
          eventId={e.id}
          label={labelOf(picked)}
          ids={picked.ids}
          count={picked.value}
          show={picked.key === 'dietary' ? 'dietary' : 'needs'}
          onClose={() => setPicked(null)}
          onOpenPeople={
            go
              ? () => {
                  setPicked(null);
                  go('before', 'people');
                }
              : undefined
          }
        >
          {picked.key.startsWith('speakers') && d.speakers?.notOnList?.length ? (
            <div className="mt-6">
              <p className="text-small text-ink-3">On the agenda, not on the event list:</p>
              <ul className="mt-1">
                {d.speakers.notOnList.map((n) => (
                  <li key={n} className="border-b border-line py-[11px] text-ink">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </PeopleSheet>
      ) : null}
    </div>
  );
}
